import { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { CheckCircle, AlertCircle, ArrowRight, Loader } from 'lucide-react'
import { validateTournament } from '../../../lib/tournamentValidation'

export const TournamentSetupProgress = () => {
    const { tournamentId } = useParams()
    const location = useLocation()
    const [status, setStatus] = useState(null)
    const [loading, setLoading] = useState(true)
    const [minimized, setMinimized] = useState(localStorage.getItem('setup_minimized') === 'true')

    const fetchStatus = async () => {
        if (!tournamentId) return
        setLoading(true)
        const result = await validateTournament(tournamentId)
        setStatus(result)
        setLoading(false)
    }

    // Refetch on route change (in case user added items)
    useEffect(() => {
        fetchStatus()
    }, [tournamentId, location.pathname])

    if (!tournamentId || loading) return null
    if (status?.isReady) return null // Hide if setup is complete

    if (minimized) {
        return (
            <div className="mb-6 glass-strong rounded-xl p-3 flex items-center justify-between border-l-4 border-orange-500 animate-fadeIn">
                <div className="flex items-center gap-3">
                    <AlertCircle className="text-orange-500" size={20} />
                    <div>
                        <p className="font-bold text-gray-800 text-sm">Setup Incomplete</p>
                        <p className="text-xs text-gray-500">{status.blocker?.label} required</p>
                    </div>
                </div>
                <button
                    onClick={() => { setMinimized(false); localStorage.setItem('setup_minimized', 'false') }}
                    className="text-orange-600 text-sm font-semibold hover:underline"
                >
                    Show Checklist
                </button>
            </div>
        )
    }

    return (
        <div className="mb-6 glass-strong rounded-2xl p-6 border border-orange-500/30 relative overflow-hidden animate-slideDown">
            <div className="absolute top-0 left-0 w-2 h-full bg-orange-500"></div>

            <div className="flex justify-between items-start mb-4 pl-4">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <AlertCircle className="text-orange-500" size={20} />
                        Tournament Setup Required
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                        Complete these steps to generate matches.
                    </p>
                </div>
                <button
                    onClick={() => { setMinimized(true); localStorage.setItem('setup_minimized', 'true') }}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                >
                    Minimize
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pl-4">
                {status.steps.map((step) => (
                    <div
                        key={step.id}
                        className={`relative p-3 rounded-lg border transition-all ${step.isComplete
                            ? 'bg-green-50/50 border-green-200'
                            : 'bg-white/50 border-gray-200 hover:border-orange-300'
                            }`}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-semibold ${step.isComplete ? 'text-green-700' : 'text-gray-700'}`}>
                                {step.label}
                            </span>
                            {step.isComplete ? (
                                <CheckCircle size={16} className="text-green-500" />
                            ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                            )}
                        </div>

                        {!step.isComplete && (
                            <Link
                                to={step.link}
                                className="text-xs flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium mt-2"
                            >
                                {step.action} <ArrowRight size={12} />
                            </Link>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
